import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLiveGames } from '../../api/playing';
import { subscribeLiveLobby } from '../../playing/chessSocket';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import Button from '../../components/common/Button';
import { GameListRow, PlayingBanner, PlayingEmptyState, PlayingSkeleton } from './playingUi';
import { getActiveRole, getRoles } from '../../utils/auth';

const SPEED_FILTERS = ['ALL', 'BULLET', 'BLITZ', 'RAPID', 'CLASSICAL', 'CORRESPONDENCE'];

function isLiveListable(g) {
  if (!g?.id) return false;
  if (g.gameType === 'VS_COMPUTER') return false;
  if (g.spectatorAllowed === false) return false;
  return g.status === 'ACTIVE' || g.status === 'PENDING';
}

function parseSpeedParam(raw) {
  const v = String(raw || '').toUpperCase();
  return SPEED_FILTERS.includes(v) ? v : 'ALL';
}

export default function LiveGamesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isParentRole = String(getActiveRole() || '').toUpperCase() === 'PARENT'
    || (getRoles() || []).some((r) => String(r).toUpperCase().replace(/^ROLE_/, '') === 'PARENT');
  const [searchParams, setSearchParams] = useSearchParams();
  const [games, setGames] = useState([]);
  const [speedFilter, setSpeedFilter] = useState(() => parseSpeedParam(searchParams.get('speed')));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [wsStatus, setWsStatus] = useState('connecting');

  const setSpeed = (sc) => {
    setSpeedFilter(sc);
    const next = new URLSearchParams(searchParams);
    if (sc === 'ALL') next.delete('speed');
    else next.set('speed', sc);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const fromUrl = parseSpeedParam(searchParams.get('speed'));
    setSpeedFilter((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [searchParams]);

  const load = (isRefresh = false) =>
    getLiveGames()
      .then((rows) => {
        setGames(Array.isArray(rows) ? rows : []);
        setError('');
      })
      .catch(() => setError(t('playing.loadFailed')))
      .finally(() => {
        if (!isRefresh) setLoading(false);
      });

  useEffect(() => {
    load(false);
    const unsub = subscribeLiveLobby((g) => {
      if (!g?.id) {
        load(true);
        return;
      }
      setGames((prev) => {
        const without = prev.filter((x) => Number(x.id) !== Number(g.id));
        if (!isLiveListable(g)) return without;
        return [g, ...without].slice(0, 40);
      });
    }, setWsStatus);
    return unsub;
  }, [t]);

  useEffect(() => {
    if (wsStatus !== 'disconnected') return undefined;
    let delay = 5000;
    let timer = null;
    let cancelled = false;
    const tick = () => {
      load(true).finally(() => {
        if (cancelled) return;
        delay = Math.min(delay * 1.5, 30000);
        timer = setTimeout(tick, delay + Math.floor(Math.random() * 800));
      });
    };
    timer = setTimeout(tick, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [wsStatus]);

  const filteredGames = useMemo(() => {
    if (speedFilter === 'ALL') return games;
    return games.filter((g) => String(g.speedClass || '').toUpperCase() === speedFilter);
  }, [games, speedFilter]);

  return (
    <div className="page-shell-form playing-page max-w-3xl">
      <PageHeaderSection
        eyebrow={t('nav.playingPlatform')}
        title={t('playing.liveGames')}
        subtitle={t('playing.liveGamesSubtitle')}
      />
      {error && (
        <PlayingBanner tone="danger">
          {error}
          {' '}
          <button
            type="button"
            className="font-semibold underline"
            onClick={() => {
              setLoading(true);
              load(false);
            }}
          >
            {t('playing.retry', { defaultValue: 'Retry' })}
          </button>
        </PlayingBanner>
      )}
      {wsStatus === 'disconnected' && (
        <PlayingBanner tone="warning">{t('playing.reconnecting')}</PlayingBanner>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {SPEED_FILTERS.map((sc) => (
          <button
            key={sc}
            type="button"
            onClick={() => setSpeed(sc)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              speedFilter === sc ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700'
            }`}
          >
            {sc === 'ALL'
              ? t('playing.allSpeeds', { defaultValue: 'All' })
              : t(`playing.speed.${sc}`, {
                defaultValue: sc.charAt(0) + sc.slice(1).toLowerCase(),
              })}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="list-table-card p-4">
          <PlayingSkeleton rows={4} />
        </div>
      ) : games.length === 0 && !error ? (
        <div className="list-table-card">
          <PlayingEmptyState
            title={t('playing.noLiveGames')}
            hint={t('playing.noLiveGamesHint')}
            action={(
              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="page-primary" onClick={() => navigate('/playing')}>
                  {t('playing.playNow')}
                </Button>
                {!isParentRole && (
                  <Button variant="page-secondary" onClick={() => navigate('/playing/challenge')}>
                    {t('playing.challengePlayer')}
                  </Button>
                )}
              </div>
            )}
          />
        </div>
      ) : filteredGames.length === 0 && !error ? (
        <div className="list-table-card">
          <PlayingEmptyState
            title={t('playing.noLiveGamesForSpeed', {
              defaultValue: 'No live games in this speed class',
            })}
            hint={t('playing.tryAnotherSpeedFilter', {
              defaultValue: 'Try another speed filter or clear to All.',
            })}
            action={(
              <Button variant="page-secondary" onClick={() => setSpeed('ALL')}>
                {t('playing.allSpeeds', { defaultValue: 'All' })}
              </Button>
            )}
          />
        </div>
      ) : filteredGames.length === 0 ? null : (
        <ul className="list-table-card divide-y divide-slate-100 overflow-hidden">
          {filteredGames.map((g) => (
            <GameListRow
              key={g.id}
              game={g}
              href={`/playing/spectate/${g.id}`}
              actionLabel={t('playing.spectate')}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
